// 自動儲存：目前存瀏覽器草稿；登入後（帳號功能）改存雲端，介面相同。
import { parseOutline, serializeOutline, type MindMap } from '@sind/core';
import type { EditorState } from './state.js';
import { t } from './i18n.js';

const DRAFT_KEY = 'sind-editor-draft';

export interface MapStorage {
  /** 儲存目前的圖；失敗時丟出錯誤 */
  save(map: MindMap): Promise<void>;
  /** 成功儲存後顯示的狀態文字 */
  readonly savedLabel: string;
}

// 瀏覽器草稿（localStorage）。
export const localDraft: MapStorage & { load(): MindMap | null; clear(): void } = {
  savedLabel: t('status.local'),
  async save(map) {
    localStorage.setItem(DRAFT_KEY, serializeOutline(map, { ids: 'all' }));
  },
  load() {
    try {
      const text = localStorage.getItem(DRAFT_KEY);
      if (!text) return null;
      return parseOutline(text).map;
    } catch {
      return null;
    }
  },
  clear() {
    try { localStorage.removeItem(DRAFT_KEY); } catch { /* 忽略 */ }
  },
};

// 依版本號去抖動儲存，並更新狀態文字。
export class AutoSaver {
  private timer = 0;
  private savedRevision: number;
  private saving = false;

  constructor(private readonly state: EditorState, public storage: MapStorage, private readonly status: HTMLElement, private readonly delay = 1200) {
    this.savedRevision = state.revision;
    this.setStatus(storage.savedLabel, 'saved');
  }

  schedule(): void {
    if (this.state.revision === this.savedRevision) return;
    clearTimeout(this.timer);
    this.timer = window.setTimeout(() => void this.flush(), this.delay);
  }

  async flush(): Promise<void> {
    clearTimeout(this.timer);
    if (this.saving || this.state.revision === this.savedRevision) return;
    const revision = this.state.revision;
    this.saving = true;
    this.setStatus(t('status.saving'), 'saving');
    try {
      await this.storage.save(this.state.map);
      this.savedRevision = revision;
      this.setStatus(this.storage.savedLabel, 'saved');
    } catch (error) {
      console.error(error);
      this.setStatus(t('status.error'), 'error');
    } finally {
      this.saving = false;
    }
    // 儲存期間又有修改時再存一次。
    if (this.state.revision !== this.savedRevision) this.schedule();
  }

  /** 換一張圖或換儲存位置後，以目前版本為已儲存基準 */
  reset(storage: MapStorage = this.storage): void {
    this.storage = storage;
    this.savedRevision = this.state.revision;
    this.setStatus(storage.savedLabel, 'saved');
  }

  private setStatus(text: string, kind: 'saved' | 'saving' | 'error'): void {
    this.status.textContent = text;
    this.status.dataset.kind = kind;
  }
}
